import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IconComponent } from '@examdojo/core/icon';
import { MarkdownViewerComponent } from '@examdojo/core/ui/markdown-viewer';

import { buildAlignedFormula, buildPluggedInFormula } from './measures-formula.util';
import { type MeasureId, type MeasureResult, MEASURE_COLORS, MEASURE_DEFS } from './measures.model';
import { formatMeasureValue, type MeasureValues } from './measures.util';

@Component({
  selector: 'dojo-measures-panel',
  standalone: true,
  imports: [MatExpansionModule, MatTooltipModule, IconComponent, MarkdownViewerComponent],
  templateUrl: './measures-panel.component.html',
  styleUrl: './measures-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeasuresPanelComponent {
  readonly sortedData = input.required<number[]>();
  readonly measureValues = input.required<MeasureValues>();
  readonly pinnedMeasures = input.required<ReadonlySet<MeasureId>>();

  readonly measureHover = output<MeasureId | null>();
  readonly measurePin = output<MeasureId>();
  readonly measureUnpin = output<MeasureId>();

  readonly measureResults = computed<MeasureResult[]>(() => {
    const sortedData = this.sortedData();
    const measures = this.measureValues();
    if (sortedData.length === 0) {
      return [];
    }

    return MEASURE_DEFS.map((def) => ({
      id: def.id,
      value: measures.get(def.id) ?? null,
      formattedValue: formatMeasureValue(def.id, measures.get(def.id)),
      pluggedInLatex: buildAlignedFormula(def.formulaLatex, buildPluggedInFormula(def.id, sortedData, measures)),
    }));
  });

  readonly standardMeasures = computed(() => this.measureResults().filter((r) => r.id !== 'skewness'));

  readonly skewnessResult = computed(() => this.measureResults().find((r) => r.id === 'skewness') ?? null);

  readonly skewnessClassification = computed(() => {
    const result = this.skewnessResult();
    if (!result || typeof result.value !== 'number') {
      return { text: '—', thresholdLatex: '' };
    }
    const v = result.value;
    const abs = Math.abs(v);
    if (abs < 0.5) {
      return { text: 'Approximately symmetric', thresholdLatex: '$|\\gamma_1| < 0.5$' };
    }
    if (abs < 1) {
      const dir = v < 0 ? 'left' : 'right';
      return { text: `Moderately ${dir}-skewed`, thresholdLatex: '$0.5 \\leq |\\gamma_1| < 1$' };
    }
    const dir = v < 0 ? 'left' : 'right';
    return { text: `Strongly ${dir}-skewed`, thresholdLatex: '$|\\gamma_1| \\geq 1$' };
  });

  readonly skewnessInsights = computed<string[]>(() => {
    const measures = this.measureValues();
    const skew = this.skewnessResult();
    if (!skew || typeof skew.value !== 'number') {
      return [];
    }

    const v = skew.value;
    const abs = Math.abs(v);
    const meanVal = measures.get('mean');
    const medianVal = measures.get('median');
    const mean = typeof meanVal === 'number' ? meanVal : undefined;
    const median = typeof medianVal === 'number' ? medianVal : undefined;

    const insights: string[] = [];

    if (typeof mean === 'number' && typeof median === 'number') {
      if (abs < 0.5) {
        insights.push(
          'The mean and median are close together $\\rightarrow$ the distribution is approximately symmetric.',
        );
      } else if (v < 0) {
        insights.push(
          'The mean is less than the median $\\rightarrow$ the distribution is left-skewed (negatively skewed).',
        );
      } else {
        insights.push(
          'The mean is greater than the median $\\rightarrow$ the distribution is right-skewed (positively skewed).',
        );
      }
    }

    if (abs < 0.5) {
      insights.push('A skewness value close to $0$ means the data is roughly evenly spread on both sides of the mean.');
    } else if (abs < 1) {
      insights.push(
        `The tail on the ${v < 0 ? 'left' : 'right'} side is somewhat longer. Most data is concentrated on the ${
          v < 0 ? 'right' : 'left'
        }.`,
      );
    } else {
      insights.push(
        `The tail on the ${
          v < 0 ? 'left' : 'right'
        } side is significantly longer, indicating a strong asymmetry in the distribution.`,
      );
    }

    return insights;
  });

  readonly skewnessScale = computed(() => {
    const result = this.skewnessResult();
    if (!result || typeof result.value !== 'number') {
      return { position: 50, zone: 'symmetric' as const };
    }
    const v = result.value;
    const abs = Math.abs(v);

    type Zone = 'strong-left' | 'mod-left' | 'symmetric' | 'mod-right' | 'strong-right';
    let zone: Zone;
    if (abs < 0.5) {
      zone = 'symmetric';
    } else if (abs < 1) {
      zone = v < 0 ? 'mod-left' : 'mod-right';
    } else {
      zone = v < 0 ? 'strong-left' : 'strong-right';
    }

    // Position on -2 to 2 scale
    const clamped = Math.max(-2, Math.min(2, v));
    const position = ((clamped + 2) / 4) * 100;

    return { position, zone };
  });

  readonly measureInsights = computed<ReadonlyMap<MeasureId, string[]>>(() => {
    const measures = this.measureValues();
    const data = this.sortedData();
    const n = data.length;
    const map = new Map<MeasureId, string[]>();

    if (n === 0) {
      return map;
    }

    const mean = getNumericMeasure(measures, 'mean');
    const stddev = getNumericMeasure(measures, 'stddev');
    const variance = getNumericMeasure(measures, 'variance');
    const q1 = getNumericMeasure(measures, 'q1');
    const q3 = getNumericMeasure(measures, 'q3');
    const iqr = getNumericMeasure(measures, 'iqr');

    const range = getNumericMeasure(measures, 'range');

    // --- Standard Deviation ---
    if (typeof mean === 'number' && typeof stddev === 'number' && mean !== 0) {
      const cv = (stddev / Math.abs(mean)) * 100;
      const cvLabel = cv < 15 ? 'low' : cv < 30 ? 'moderate' : 'high';

      const within1 = data.filter((v) => v >= mean - stddev && v <= mean + stddev).length;
      const pct1 = ((within1 / n) * 100).toFixed(0);

      map.set('stddev', [
        `A typical data point is about $${fmt(stddev)}$ units away from the mean of $\\mu = ${fmt(
          mean,
        )}$. That's $${fmt(cv)}\\%$ of the mean, indicating ${cvLabel} variability.`,
        `In this dataset, $${pct1}\\%$ of values ($${within1}/${n}$) fall within $\\mu \\pm \\sigma = [${fmt(
          mean - stddev,
        )},\\, ${fmt(mean + stddev)}]$. For a perfectly normal distribution this would be $\\approx 68\\%$.`,
      ]);
    }

    // --- Variance ---
    if (typeof variance === 'number' && typeof stddev === 'number' && typeof mean === 'number') {
      const insights: string[] = [
        `On average, each data point's squared distance from the mean ($\\mu = ${fmt(mean)}$) is $\\sigma^2 = ${fmt(
          variance,
        )}$. Taking the square root gives $\\sigma = ${fmt(stddev)}$, which is in the same units as the data.`,
      ];

      if (mean !== 0) {
        const cv = (stddev / Math.abs(mean)) * 100;
        if (cv < 15) {
          insights.push(
            `With $\\sigma = ${fmt(stddev)}$ relative to $\\mu = ${fmt(
              mean,
            )}$, the data is tightly clustered around the mean.`,
          );
        } else if (cv < 30) {
          insights.push(
            `With $\\sigma = ${fmt(stddev)}$ relative to $\\mu = ${fmt(
              mean,
            )}$, the data shows a moderate amount of spread.`,
          );
        } else {
          insights.push(
            `With $\\sigma = ${fmt(stddev)}$ relative to $\\mu = ${fmt(
              mean,
            )}$, the data is widely spread — values vary significantly from the mean.`,
          );
        }
      }

      map.set('variance', insights);
    }

    // --- IQR ---
    if (typeof q1 === 'number' && typeof q3 === 'number' && typeof iqr === 'number') {
      const lowerFence = q1 - 1.5 * iqr;
      const upperFence = q3 + 1.5 * iqr;
      const outlierCount = data.filter((v) => v < lowerFence || v > upperFence).length;

      const insights: string[] = [];

      if (typeof range === 'number' && range > 0) {
        const iqrRatio = ((iqr / range) * 100).toFixed(0);
        insights.push(
          `The middle $50\\%$ of data spans $${fmt(iqr)}$ units (from $Q_1 = ${fmt(q1)}$ to $Q_3 = ${fmt(
            q3,
          )}$), which is $${iqrRatio}\\%$ of the total range ($${fmt(range)}$). ${
            Number(iqrRatio) < 30
              ? 'The data is highly concentrated in the centre with long tails or outliers.'
              : Number(iqrRatio) > 50
                ? 'The data is fairly evenly spread across the range.'
                : 'The data has a moderate concentration around the centre.'
          }`,
        );
      }

      insights.push(
        `Outlier fences: $< ${fmt(lowerFence)}$ or $> ${fmt(
          upperFence,
        )}$ ($Q_1 - 1.5 \\cdot \\text{IQR}$ and $Q_3 + 1.5 \\cdot \\text{IQR}$). ${
          outlierCount > 0
            ? `$${outlierCount}$ value${outlierCount > 1 ? 's' : ''} in this dataset ${
                outlierCount > 1 ? 'fall' : 'falls'
              } outside these fences.`
            : 'No values in this dataset fall outside these fences.'
        }`,
      );

      map.set('iqr', insights);
    }

    return map;
  });

  readonly measureColors = MEASURE_COLORS;

  isPinned(id: MeasureId): boolean {
    return this.pinnedMeasures().has(id);
  }

  getDef(id: MeasureId): (typeof MEASURE_DEFS)[number] {
    return MEASURE_DEFS.find((d) => d.id === id)!;
  }

  onMouseEnter(id: MeasureId): void {
    this.measureHover.emit(id);
  }

  onMouseLeave(): void {
    this.measureHover.emit(null);
  }

  onPanelOpened(id: MeasureId): void {
    this.measurePin.emit(id);
  }

  onPanelClosed(id: MeasureId): void {
    this.measureUnpin.emit(id);
  }
}

function fmt(v: number): string {
  if (Number.isInteger(v)) {
    return String(v);
  }
  return parseFloat(v.toFixed(2)).toString();
}

/** Type guard that extracts a scalar number from MeasureValues, filtering out number[] values. */
function getNumericMeasure(measures: MeasureValues, id: MeasureId): number | undefined {
  const val = measures.get(id);
  return typeof val === 'number' ? val : undefined;
}
